import type { ReactNode, JSX } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import clsx from 'clsx'

import { GripVerticalIcon, UnstyledButton } from '@buffer-mono/popcorn'

import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import type { SavedView } from '../hooks'
import { savedViewItemStyles } from '../SavedViewItem'

import styles from './DraggableSavedViewItem.module.css'

interface DraggableSavedViewItemProps {
  view: SavedView
  disabled?: boolean
  children: ReactNode
}

/**
 * Wraps a saved-view row with `useSortable` so the row participates in the
 * surrounding DndContext. Mirrors `DraggableChannelItem` so the two
 * draggable sidebar sections share the same affordances.
 */
export function DraggableSavedViewItem({
  view,
  disabled = false,
  children,
}: DraggableSavedViewItemProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: view.id,
    disabled,
  })

  useDraggingCursor(isDragging)

  const style: React.CSSProperties & Record<`--${string}`, string> = {
    '--translate-x': transform?.x ? `${transform.x}px` : '',
    '--translate-y': transform?.y ? `${transform.y}px` : '',
    '--scale-x': transform?.scaleX ? `${transform.scaleX}` : '',
    '--scale-y': transform?.scaleY ? `${transform.scaleY}` : '',
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(styles.draggableItem, savedViewItemStyles.row, {
        [styles.dragShadow]: isDragging,
      })}
    >
      {!disabled && !isDragging && (
        <UnstyledButton
          className={styles.dragHandle}
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label={`Drag to reorder ${view.name}`}
        >
          <GripVerticalIcon />
        </UnstyledButton>
      )}
      {children}
    </li>
  )
}
